import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal, SectionEyebrow } from "@/components/shared/Reveal";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CONCEPT_TERRITORIES } from "@/lib/pulseConstants";
import SelectedWorkSection from "@/components/shared/SelectedWorkSection";

export default function WorkPage() {
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        document.title = "Work — REFORM Pulse | Creative Event Agency Malaysia";
    }, []);

    return (
        <div data-testid="work-page">
            {/* Hero */}
            <section className="relative pt-24 md:pt-28 lg:pt-32 pb-16 overflow-hidden">
                <div className="absolute inset-0 -z-10">
                    <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1533929736458-ca588d08c8be?auto=format&fit=crop&w=2000&q=80')` }} />
                    <div className="absolute inset-0 hero-radial" />
                    <div className="absolute inset-0 bg-[#07070A]/65" />
                </div>
                <div className="container-editorial py-16">
                    <SectionEyebrow index="//" label="Work" />
                    <Reveal>
                        <h1 className="display-1 text-white text-balance max-w-4xl">
                            What we <span className="text-[#FF2A2A]">can build</span> — next.
                        </h1>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="body-lg mt-6 max-w-2xl">
                            As an experiential marketing agency in Malaysia, we design activations to be
                            photographed, filmed and shared. Below are the world-building formats we start
                            with — real case studies live under NDA and are shared during your strategy call.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Selected Work — real case studies (future-ready, currently on request) */}
            <SelectedWorkSection />

            {/* Concepts grid */}
            <section className="section-pad border-t border-white/10">
                <div className="container-editorial">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                        <div>
                            <SectionEyebrow index="//" label="Concept Territories" />
                            <Reveal>
                                <h2 className="display-2 text-white text-balance">
                                    Format <span className="text-[#FF2A2A]">starting points</span>.
                                </h2>
                            </Reveal>
                        </div>
                        <Reveal delay={0.1}>
                            <p className="body-md max-w-md">
                                Concept fragments — not case studies. Use these as jump-off points when you
                                brief us on a launch, pop-up, PR moment or creator night.
                            </p>
                        </Reveal>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                        {CONCEPT_TERRITORIES.map((c, i) => (
                            <Reveal key={c.key} delay={0.04 * i}>
                                <motion.button
                                    whileHover={{ y: -4 }}
                                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                    onClick={() => setSelected(c)}
                                    className="text-left w-full h-full rounded-2xl border border-white/10 bg-[#0B0C10] overflow-hidden hover:border-white/25 transition-colors group"
                                    data-testid={`work-concept-card-${c.key}`}
                                >
                                    <div className="editorial-image aspect-[4/3] relative">
                                        <img src={c.image} alt={c.title} loading="lazy" />
                                        <div className="absolute top-4 left-4 z-10">
                                            <span className="px-2.5 py-1 rounded-full bg-black/60 backdrop-blur border border-white/15 text-[10px] font-mono uppercase tracking-widest text-white/85">
                                                Concept • not case study
                                            </span>
                                        </div>
                                    </div>
                                    <div className="p-6">
                                        <div className="font-mono text-[11px] uppercase tracking-widest text-white/50 mb-2">
                                            Format / 0{i + 1}
                                        </div>
                                        <div className="flex items-start justify-between gap-4">
                                            <h3 className="font-display text-2xl md:text-3xl text-white leading-tight">
                                                {c.title}
                                            </h3>
                                            <div className="h-8 w-8 rounded-full border border-white/15 flex-shrink-0 flex items-center justify-center text-white/60 group-hover:text-[#FF2A2A] group-hover:border-[#FF2A2A]/60 transition-colors">
                                                <ArrowUpRight size={14} />
                                            </div>
                                        </div>
                                        <p className="text-sm text-white/70 leading-relaxed mt-3">{c.blurb}</p>
                                    </div>
                                </motion.button>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="section-pad border-t border-white/10 text-center">
                <div className="container-editorial">
                    <Reveal>
                        <h2 className="display-2 text-white text-balance">
                            Have a moment you want to build?
                        </h2>
                    </Reveal>
                    <div className="mt-8">
                        <Button asChild className="cta-glow bg-[#FF2A2A] hover:bg-[#ff4747] text-black font-semibold rounded-full h-14 px-8 text-base">
                            <Link to="/contact" data-testid="work-cta-brief" className="inline-flex items-center gap-2">
                                Start Your Pulse Brief <ArrowUpRight size={18} />
                            </Link>
                        </Button>
                    </div>
                </div>
            </section>

            <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <DialogContent className="bg-[#0B0C10] border border-white/10 text-white max-w-2xl">
                    {selected && (
                        <div>
                            <div className="editorial-image aspect-[16/9] rounded-xl overflow-hidden mb-6">
                                <img src={selected.image} alt={selected.title} />
                            </div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">
                                Concept Territory
                            </div>
                            <DialogTitle asChild>
                                <h3 className="font-display text-4xl text-white mb-3 uppercase">{selected.title}</h3>
                            </DialogTitle>
                            <DialogDescription asChild>
                                <p className="body-md text-white/80">{selected.blurb}</p>
                            </DialogDescription>
                            <div className="mt-6 flex items-center gap-3">
                                <span className="px-3 py-1.5 rounded-full border border-[#FF2A2A]/40 bg-[#FF2A2A]/10 text-xs font-mono uppercase tracking-widest text-[#FF2A2A]">
                                    Unlocks: {selected.unlocks}
                                </span>
                            </div>
                            <div className="mt-8 flex gap-3">
                                <Button asChild className="bg-[#FF2A2A] hover:bg-[#ff4747] text-black rounded-full h-11 px-5">
                                    <Link to="/contact" onClick={() => setSelected(null)}>Brief us on this</Link>
                                </Button>
                                <Button variant="outline" onClick={() => setSelected(null)} className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/5 h-11 px-5">
                                    Close
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
